import { APIURL } from "variables/constants";
import { DIGITAL_EDITABLE_KEYS } from "./greatDigitalEdit";

const DIGITAL_DB_CHANNELS = ["email", "live_chat", "sms"];

const normalizeDigitalRowsPayload = (json) => {
  if (Array.isArray(json)) return json;
  if (Array.isArray(json?.rows)) return json.rows;
  if (Array.isArray(json?.data)) return json.data;
  return [];
};

/** Same fetch/parse pattern as voice GREAT (index.jsx fetchData) — public API, no VPN. */
const fetchDigitalFromApi = async ({ channel, fromdate, todate } = {}) => {
  const params = new URLSearchParams();
  params.set("query", "great_digital_data");
  if (channel) params.set("channel", channel);
  if (fromdate) params.set("fromdate", fromdate);
  if (todate) params.set("todate", todate);

  const url = `${APIURL}?${params.toString()}`;
  const resp = await fetch(url);
  if (!resp.ok) throw new Error(`HTTP ${resp.status}`);

  const text = await resp.text();
  let json;
  try {
    json = JSON.parse(text);
  } catch {
    throw new Error(`Invalid JSON response from API. Response: ${text.substring(0, 100)}`);
  }

  if (json && typeof json === "object" && !Array.isArray(json) && json.error) {
    throw new Error(json.error);
  }
  if (json === null || json === undefined) {
    throw new Error(
      "Digital QA is not enabled on the API yet (great_digital_data). Add the handlers in php/great_digital_ai_dashboard_snippet.php to ai_dashboard.php on api.gabrielny.com."
    );
  }
  return normalizeDigitalRowsPayload(json);
};

/** Loads digital QA via ai_dashboard.php — same server path as inbound/outbound great_data. */
export const fetchDigitalRows = async ({ channel, fromdate, todate } = {}) => {
  return fetchDigitalFromApi({ channel, fromdate, todate });
};

export const fetchDigitalChannelRows = async (channel, dateRange) => {
  const fromdate = dateRange?.fromdate;
  const todate = dateRange?.todate;
  return fetchDigitalRows({ channel, fromdate, todate });
};

/** Summary: one request (all channels) when possible; else parallel per channel. */
export const fetchAllDigitalRows = async (dateRange) => {
  const fromdate = dateRange?.fromdate;
  const todate = dateRange?.todate;
  try {
    const all = await fetchDigitalRows({ fromdate, todate });
    if (all.length > 0) return all;
  } catch (e) {
    console.warn("Digital GREAT all-rows fetch:", e.message);
  }

  const parts = await Promise.all(
    DIGITAL_DB_CHANNELS.map((channel) =>
      fetchDigitalRows({ channel, fromdate, todate }).catch((err) => {
        console.warn(`Digital GREAT fetch (${channel}):`, err.message);
        return [];
      })
    )
  );
  const merged = parts.flat();
  if (merged.length === 0) {
    throw new Error("Failed to load digital QA data from the API");
  }
  return merged;
};

const sanitizePayload = (data) => {
  const out = {};
  Object.entries(data || {}).forEach(([key, val]) => {
    if (DIGITAL_EDITABLE_KEYS.has(key)) out[key] = val;
  });
  return out;
};

export const updateDigitalRow = async (sr_no, data) => {
  const payload = sanitizePayload(data);
  const resp = await fetch(APIURL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ query: "great_digital_data_update", sr_no, data: payload }),
  });
  const text = await resp.text();
  let result;
  try {
    result = JSON.parse(text);
  } catch {
    throw new Error(resp.ok ? "Invalid server response" : `HTTP ${resp.status}`);
  }
  if (!resp.ok || result?.error) throw new Error(result?.error || `HTTP ${resp.status}`);
  if (result === null) {
    throw new Error("Digital QA update is not enabled on the API (great_digital_data_update).");
  }
  return result;
};

export const deleteDigitalRow = async (sr_no) => {
  const resp = await fetch(APIURL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ query: "great_digital_data_delete", sr_no }),
  });
  const text = await resp.text();
  let result;
  try {
    result = JSON.parse(text);
  } catch {
    throw new Error(resp.ok ? "Invalid server response" : `HTTP ${resp.status}`);
  }
  if (!resp.ok || result?.error) throw new Error(result?.error || `HTTP ${resp.status}`);
  if (result === null) {
    throw new Error("Digital QA delete is not enabled on the API (great_digital_data_delete).");
  }
  return result;
};
